/**
 * Rute panela (TanStack Router, tipizirano, code-based).
 *
 * Javne auth rute: /prijava, /registracija, /zaboravljena-lozinka, /reset-lozinke?token=,
 * /potvrda-email?token=, /poziv?token=, /otkazi-brisanje?token=, /promjena-email?token=,
 * /auth/callback/$provider (Google/Apple), /povezivanje-naloga?token=. Sve ostalo je iza prijave (layout `app`).
 */
import type { Phase, ProjectSort, TenantStatus } from '@sm/api';
import { isApiClientError } from '@sm/api';
import { PHASE_KEYS, TENANT_STATUSES } from '@sm/core';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  notFound,
  Outlet,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';

import { AppShell } from './components/shell/AppShell';
import { RouteErrorScreen } from './components/shell/CrashScreen';
import { NotFoundPage } from './components/shell/NotFoundPage';
import { Toaster } from './components/shell/Toaster';
import { AuthLayout } from './features/auth/AuthLayout';
import { LoginPage } from './features/auth/LoginPage';
import { setUnauthorizedHandler } from './lib/api';
import { PLATFORM_LOGIN_PATH, setPlatformUnauthorizedHandler } from './lib/platformApi';
import { platformMeQuery } from './lib/platformQueries';
import { setPlanLimitLookup } from './lib/toast';
import { SOON_MODULES } from './lib/domain-ui';
import { featureState } from './lib/permissions';
import { syncFromServer } from './lib/preferences';
import { meQuery, subscriptionQuery } from './lib/queries';

export interface RouterContext {
  queryClient: QueryClient;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
const num = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
};
const oneOf = <T extends string>(v: unknown, all: readonly T[]): T | undefined =>
  typeof v === 'string' && (all as readonly string[]).includes(v) ? (v as T) : undefined;

/** Sve search vrijednosti su opcione (Link bez `search` je ispravan). */
const optional =
  <T extends object>(fn: (s: Record<string, unknown>) => T) =>
  (s: Record<string, unknown>): Partial<T> => {
    const out = fn(s);
    return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined)) as Partial<T>;
  };

/** Moduli iz paketa (`production`, `analytics`): flag `off` znači da ruta ne postoji (404). */
function requirePlanFeature(key: 'production' | 'analytics') {
  return ({ context }: { context: RouterContext }): void => {
    if (context.queryClient.getQueryData(meQuery.queryKey)?.features[key] === 'off')
      throw notFound();
  };
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Outlet />
      <Toaster />
    </>
  ),
  notFoundComponent: NotFoundPage,
});

// ---------------------------------------------------------------- javne auth rute
const authLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: AuthLayout,
});

const tokenSearch = optional((s) => ({ token: str(s.token) }));

export const loginRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/prijava',
  validateSearch: optional((s) => ({
    redirect: str(s.redirect),
    mode: oneOf(s.mode, ['platform'] as const),
    reason: oneOf(s.reason, ['expired', 'logout', 'deleted', 'deleted_company'] as const),
    /** Datum brisanja (ISO) uz `reason=deleted`. */
    until: str(s.until),
  })),
  component: LoginPage,
});
export const registerRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/registracija',
  /** `identity=1`: registracija sa Google/Apple identitetom (tiket u sessionStorage, vidi `oidc.ts`). */
  validateSearch: optional((s) => ({
    identity: s.identity === 1 || s.identity === '1' ? (1 as const) : undefined,
  })),
  component: lazyRouteComponent(() => import('./features/auth/RegisterPage'), 'RegisterPage'),
});
export const forgotRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/zaboravljena-lozinka',
  component: lazyRouteComponent(
    () => import('./features/auth/ForgotPasswordPage'),
    'ForgotPasswordPage',
  ),
});
export const resetRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/reset-lozinke',
  validateSearch: optional((s) => ({
    ...tokenSearch(s),
    platform: s.platform === 1 || s.platform === '1' ? (1 as const) : undefined,
  })),
  component: lazyRouteComponent(
    () => import('./features/auth/ResetPasswordPage'),
    'ResetPasswordPage',
  ),
});
export const verifyRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/potvrda-email',
  validateSearch: optional((s) => ({ token: str(s.token), email: str(s.email) })),
  component: lazyRouteComponent(() => import('./features/auth/VerifyEmailPage'), 'VerifyEmailPage'),
});
export const inviteRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/poziv',
  validateSearch: tokenSearch,
  component: lazyRouteComponent(() => import('./features/auth/InvitePage'), 'InvitePage'),
});
export const cancelDeletionRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/otkazi-brisanje',
  validateSearch: tokenSearch,
  component: lazyRouteComponent(
    () => import('./features/auth/CancelDeletionPage'),
    'CancelDeletionPage',
  ),
});
export const confirmEmailChangeRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/promjena-email',
  validateSearch: tokenSearch,
  component: lazyRouteComponent(
    () => import('./features/auth/ConfirmEmailChangePage'),
    'ConfirmEmailChangePage',
  ),
});

export const oidcCallbackRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/auth/callback/$provider',
  validateSearch: optional((s) => ({
    code: str(s.code),
    state: str(s.state),
    error: str(s.error),
  })),
  component: lazyRouteComponent(
    () => import('./features/auth/OidcCallbackPage'),
    'OidcCallbackPage',
  ),
});
export const confirmIdentityLinkRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/povezivanje-naloga',
  validateSearch: tokenSearch,
  component: lazyRouteComponent(
    () => import('./features/auth/ConfirmIdentityLinkPage'),
    'ConfirmIdentityLinkPage',
  ),
});

// ---------------------------------------------------------------- panel (iza prijave)
export const appLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await context.queryClient.ensureQueryData(meQuery);
      syncFromServer(me);
      return { me };
    } catch (e) {
      if (isApiClientError(e) && e.status === 401) {
        throw redirect({
          to: '/prijava',
          search: {
            redirect: location.href === '/' ? undefined : location.href,
            reason: undefined,
          },
        });
      }
      throw e;
    }
  },
  component: AppShell,
});

export const dashboardRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/',
  component: lazyRouteComponent(
    () => import('./features/dashboard/DashboardPage'),
    'DashboardPage',
  ),
});

export const clientsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/kupci',
  component: lazyRouteComponent(() => import('./features/clients/ClientsPage'), 'ClientsPage'),
});

export const productsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/proizvodi',
  component: lazyRouteComponent(() => import('./features/products/ProductsPage'), 'ProductsPage'),
});

export const PROJECT_FILTERS = ['all', 'active', 'late', 'warnings'] as const;
export type ProjectFilter = (typeof PROJECT_FILTERS)[number];
const SORTS: readonly ProjectSort[] = [
  'code',
  '-code',
  'dueDate',
  '-dueDate',
  'createdAt',
  '-createdAt',
  'updatedAt',
  '-updatedAt',
  'clientName',
  '-clientName',
  'value',
  '-value',
];

export const projectsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/projekti',
  validateSearch: optional((s) => ({
    q: str(s.q),
    filter: oneOf(s.filter, PROJECT_FILTERS),
    view: oneOf(s.view, ['table', 'board'] as const),
    phase: oneOf<Phase>(s.phase, PHASE_KEYS),
    page: num(s.page),
    sort: oneOf(s.sort, SORTS),
    novi: s.novi === true || s.novi === '1' || s.novi === 1 ? (1 as const) : undefined,
  })),
  component: lazyRouteComponent(() => import('./features/projects/ProjectsPage'), 'ProjectsPage'),
});

export const PROJECT_TABS = ['overview', 'positions', 'photos', 'documents', 'history'] as const;
export const projectDetailRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/projekti/$projectId',
  validateSearch: optional((s) => ({
    tab: oneOf(s.tab, PROJECT_TABS),
    position: str(s.position),
    doc: oneOf(s.doc, ['measurement_protocol', 'production_order'] as const),
  })),
  component: lazyRouteComponent(
    () => import('./features/projects/ProjectDetailPage'),
    'ProjectDetailPage',
  ),
});

export const measurementsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/mjerenja',
  validateSearch: optional((s) => ({ m: str(s.m), position: str(s.position) })),
  component: lazyRouteComponent(
    () => import('./features/measurements/MeasurementsPage'),
    'MeasurementsPage',
  ),
});

export const photosRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/fotodokumentacija',
  validateSearch: optional((s) => ({
    cat: str(s.cat),
    photo: str(s.photo),
    project: str(s.project),
    q: str(s.q),
    page: num(s.page),
  })),
  component: lazyRouteComponent(() => import('./features/photos/PhotosPage'), 'PhotosPage'),
});

export const productionRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/proizvodnja',
  validateSearch: optional((s) => ({ order: str(s.order) })),
  // Modul iz paketa: `off` = ruta ne postoji (kao i u API-ju, `404 feature_disabled`).
  beforeLoad: requirePlanFeature('production'),
  component: lazyRouteComponent(
    () => import('./features/production/ProductionPage'),
    'ProductionPage',
  ),
});

export const analyticsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/analiza',
  beforeLoad: requirePlanFeature('analytics'),
  component: lazyRouteComponent(
    () => import('./features/analytics/AnalyticsPage'),
    'AnalyticsPage',
  ),
});

export const SETTINGS_TABS = [
  'organizacija',
  'korisnici',
  'pravila',
  'funkcije',
  'uredjaji',
  'podrska',
  'paket',
  'profil',
] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];
export const settingsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/podesavanja',
  validateSearch: optional((s) => ({
    tab: oneOf(s.tab, SETTINGS_TABS),
    /** Podrška: otvoren izvještaj (ID) i pretraga po kodu ili uređaju. */
    report: str(s.report),
    code: str(s.code),
    device: str(s.device),
    /** Uređaji: filter stanja. */
    health: oneOf(s.health, ['ok', 'caution', 'critical'] as const),
  })),
  component: lazyRouteComponent(() => import('./features/settings/SettingsPage'), 'SettingsPage'),
});

export const soonRoute = createRoute({
  getParentRoute: () => appLayout,
  path: '/uskoro/$slug',
  beforeLoad: ({ params, context }) => {
    const mod = SOON_MODULES.find((m) => m.slug === params.slug);
    const me = context.queryClient.getQueryData(meQuery.queryKey);
    // Flag `off` (ili nepoznat modul): ruta ne postoji (404), kao i u API-ju.
    if (!mod || featureState(me, mod.key) === 'off') throw notFound();
    return { module: mod };
  },
  component: lazyRouteComponent(
    () => import('./features/soon/UnderDevelopmentPage'),
    'UnderDevelopmentPage',
  ),
});

// ---------------------------------------------------------------- platforma (operater NENASAL)
// Odvojena oblast sa vlastitom sesijom (`sm_psession`), van layouta panela firme.
export const platformLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platforma/prijava',
  validateSearch: optional((s) => ({ redirect: str(s.redirect) })),
  beforeLoad: ({ location }) => {
    const search = location.search as { redirect?: string };
    throw redirect({ to: '/prijava', search: { redirect: search.redirect } });
  },
});

export const platformLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'platform',
  beforeLoad: async ({ context, location }) => {
    try {
      const admin = await context.queryClient.ensureQueryData(platformMeQuery);
      return { admin };
    } catch (e) {
      if (isApiClientError(e) && e.status === 401) {
        throw redirect({
          to: '/platforma/prijava',
          search: { redirect: location.href === '/platforma' ? undefined : location.href },
        });
      }
      throw e;
    }
  },
  component: lazyRouteComponent(
    () => import('./features/platform/PlatformLayout'),
    'PlatformLayout',
  ),
});

export const platformOverviewRoute = createRoute({
  getParentRoute: () => platformLayout,
  path: '/platforma',
  component: lazyRouteComponent(
    () => import('./features/platform/OverviewPage'),
    'PlatformOverviewPage',
  ),
});
export const platformTenantsRoute = createRoute({
  getParentRoute: () => platformLayout,
  path: '/platforma/firme',
  validateSearch: optional((s) => ({
    q: str(s.q),
    status: oneOf<TenantStatus>(s.status, TENANT_STATUSES),
    page: num(s.page),
  })),
  component: lazyRouteComponent(
    () => import('./features/platform/TenantsPage'),
    'PlatformTenantsPage',
  ),
});
export const platformTenantRoute = createRoute({
  getParentRoute: () => platformLayout,
  path: '/platforma/firme/$tenantId',
  component: lazyRouteComponent(
    () => import('./features/platform/TenantDetailPage'),
    'PlatformTenantDetailPage',
  ),
});
export const platformRegistrationsRoute = createRoute({
  getParentRoute: () => platformLayout,
  path: '/platforma/registracije',
  component: lazyRouteComponent(
    () => import('./features/platform/RegistrationsPage'),
    'PlatformRegistrationsPage',
  ),
});
export const platformPlansRoute = createRoute({
  getParentRoute: () => platformLayout,
  path: '/platforma/paketi',
  component: lazyRouteComponent(() => import('./features/platform/PlansPage'), 'PlatformPlansPage'),
});
export const platformTeamRoute = createRoute({
  getParentRoute: () => platformLayout,
  path: '/platforma/tim',
  component: lazyRouteComponent(() => import('./features/platform/TeamPage'), 'PlatformTeamPage'),
});
export const platformAuditRoute = createRoute({
  getParentRoute: () => platformLayout,
  path: '/platforma/audit',
  validateSearch: optional((s) => ({
    action: str(s.action),
    tenantId: str(s.tenantId),
    page: num(s.page),
  })),
  component: lazyRouteComponent(() => import('./features/platform/AuditPage'), 'PlatformAuditPage'),
});

const routeTree = rootRoute.addChildren([
  authLayout.addChildren([
    loginRoute,
    registerRoute,
    forgotRoute,
    resetRoute,
    verifyRoute,
    inviteRoute,
    cancelDeletionRoute,
    confirmEmailChangeRoute,
    oidcCallbackRoute,
    confirmIdentityLinkRoute,
  ]),
  appLayout.addChildren([
    dashboardRoute,
    productsRoute,
    clientsRoute,
    projectsRoute,
    projectDetailRoute,
    measurementsRoute,
    photosRoute,
    productionRoute,
    analyticsRoute,
    settingsRoute,
    soonRoute,
  ]),
  platformLoginRoute,
  platformLayout.addChildren([
    platformOverviewRoute,
    platformTenantsRoute,
    platformTenantRoute,
    platformRegistrationsRoute,
    platformPlansRoute,
    platformAuditRoute,
    platformTeamRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient, opts: { history?: RouterHistory } = {}) {
  const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultNotFoundComponent: NotFoundPage,
    defaultErrorComponent: RouteErrorScreen,
    ...(opts.history ? { history: opts.history } : {}),
  });
  setUnauthorizedHandler(() => {
    queryClient.removeQueries({ queryKey: meQuery.queryKey });
    const href = router.state.location.href;
    void router.navigate({ to: '/prijava', search: { redirect: href, reason: 'expired' } });
  });
  setPlatformUnauthorizedHandler(() => {
    queryClient.removeQueries({ queryKey: platformMeQuery.queryKey });
    const href = router.state.location.href;
    if (router.state.location.pathname.startsWith(PLATFORM_LOGIN_PATH)) return;
    void router.navigate({
      to: '/platforma/prijava',
      search: { redirect: href.startsWith('/platforma') ? href : undefined },
    });
  });
  // `403 plan_limit`: poruka sa brojem iz paketa kad je pretplata već u kešu.
  setPlanLimitLookup((key) => {
    const sub = queryClient.getQueryData(subscriptionQuery.queryKey);
    const v = sub?.limits[key as keyof typeof sub.limits];
    return typeof v === 'number' ? v : undefined;
  });
  return router;
}

export { routeTree };

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
